import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import analytics from '../services/analytics';

// Custom hook for analytics
export const useAnalytics = () => {
  const location = useLocation();
  const previousPath = useRef(location.pathname);

  // Track page views on route change
  useEffect(() => {
    if (previousPath.current !== location.pathname) {
      // End timer for previous page
      const previousPageName = previousPath.current.split('/')[1] || 'home';
      analytics.endPageTimer(previousPageName);

      // Track new page view
      analytics.trackPageView(location.pathname, {
        referrer: previousPath.current,
        search: location.search,
      });

      // Start timer for new page
      const currentPageName = location.pathname.split('/')[1] || 'home';
      analytics.startPageTimer(currentPageName);

      previousPath.current = location.pathname;
    }
  }, [location]);

  // Track events
  const track = useCallback((event, properties) => {
    analytics.track(event, properties);
  }, []);

  // Track features
  const trackFeature = useCallback((featureName, properties) => {
    analytics.trackFeature(featureName, properties);
  }, []);

  // Track exam generation
  const trackExamGeneration = useCallback((examType, properties) => {
    analytics.trackExamGeneration(examType, properties);
  }, []);

  // Track errors
  const trackError = useCallback((error, context) => {
    analytics.trackError(error, context);
  }, []);

  // Track time on specific elements
  const trackTimeOnElement = useCallback((elementName) => {
    const startTime = Date.now();
    
    return () => {
      const duration = Date.now() - startTime;
      analytics.track('element_time_spent', {
        element: elementName,
        duration_ms: duration,
        duration_seconds: Math.round(duration / 1000),
      });
    };
  }, []);

  // Track page view manually
  const trackPageView = useCallback((pageName, properties = {}) => {
    analytics.trackPageView(location.pathname, {
      ...properties,
      pageName,
    });
  }, [location.pathname]);

  // Track events (alias for consistency with LandingPage usage)
  const trackEvent = useCallback((eventName, properties) => {
    analytics.track(eventName, properties);
  }, []);

  return {
    track,
    trackEvent,
    trackPageView,
    trackFeature,
    trackExamGeneration,
    trackError,
    trackTimeOnElement,
  };
};

// Hook to track component mount/unmount
export const useComponentTracking = (componentName) => {
  const startTime = useRef(Date.now());

  useEffect(() => {
    analytics.track('component_mounted', {
      component: componentName,
    });

    return () => {
      // Copy ref value to avoid potential issues
      const mountTime = startTime.current;
      const duration = Date.now() - mountTime;
      analytics.track('component_unmounted', {
        component: componentName,
        duration_ms: duration,
        duration_seconds: Math.round(duration / 1000),
      });
    };
  }, [componentName]);
};